import {
  BoundNumberField,
  column,
  GridColumn,
  GridTable,
  numericColumn,
  simpleDataRows,
  SimpleHeaderAndData,
  Tag,
  useComputed,
  withColumnGutters,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo } from "react";
import { TradePartnerSelectField } from "src/components/autoPopulateSelects/TradePartnerSelectField";
import { TradePartnerFilter, TradePartnerOnboardingStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { tradeName } from "src/routes/projects/change-events/guided/utils";
import {
  ReviewImpact_ChangeEventFragment,
  ReviewImpact_MaterialVariantSwapFragment,
} from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/2.ReviewImpactStep.generated";
import { ProductCard } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/components/ProductCard";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { ProjectParams } from "src/routes/routesDef";
import { fail } from "src/utils";
import { ReviewImpactedPrice } from "../../components/ReviewImpactedPrice";
import { SwapProductFormInput, SwapProductFormSwap } from "../swapProductWorkflow";
import { useSaveSwapProductChangeEvent } from "../useSaveSwapProductChangeEvent";

type ProductChangesTableProps = {
  formState: WorkflowFormState<SwapProductFormInput>;
  changeEvent: ReviewImpact_ChangeEventFragment;
  /** True when the change event charges the client, i.e. the client price column applies. */
  isChargeable: boolean;
};

export function ProductChangesTable(props: ProductChangesTableProps) {
  const { formState, changeEvent, isChargeable } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const { saveChangeEvent } = useSaveSwapProductChangeEvent({ form: formState, projectId });
  const clientNoun = changeEvent.project.lotType.clientNoun;
  const tradePartnerFilter = useMemo<TradePartnerFilter>(
    () => ({
      ...(changeEvent.project.market ? { markets: [changeEvent.project.market.id] } : {}),
      onboardingStatus: [TradePartnerOnboardingStatus.Complete, TradePartnerOnboardingStatus.InProgress],
    }),
    [changeEvent],
  );

  const rows = useComputed(
    () =>
      simpleDataRows(
        changeEvent.materialVariantSwaps.map((swap, idx) => ({
          id: swap.id,
          index: idx + 1,
          swap,
          // Step 1 saves before this step renders, so every saved swap has a form row to edit.
          form: formState.swaps.rows.find((r) => r.id.value === swap.id) ?? fail(`No form row for ${swap.id}`),
        })),
      ),
    [formState, changeEvent],
  );

  return (
    <GridTable
      id="productChangesTable"
      columns={withColumnGutters(
        createColumns({ isChargeable, clientNoun, tradePartnerFilter, onUpdateSwap: saveChangeEvent }),
      )}
      rows={rows}
    />
  );
}

type ProductChangeRow = {
  id: string;
  /** The swap's 1-based position, i.e. the "#" column. */
  index: number;
  swap: ReviewImpact_MaterialVariantSwapFragment;
  form: ObjectState<SwapProductFormSwap>;
};

type Row = SimpleHeaderAndData<ProductChangeRow>;

type CreateColumnsOpts = {
  /** True when the change event charges the client, i.e. the client price column applies. */
  isChargeable: boolean;
  /** What the project calls its client, i.e. "Homeowner". */
  clientNoun: string;
  /** Narrows the trade picker to the project's market and onboarded trades. */
  tradePartnerFilter: TradePartnerFilter;
  /** Saves the swap's cost, price, or trade. */
  onUpdateSwap: () => Promise<void>;
};

function createColumns(opts: CreateColumnsOpts): GridColumn<Row>[] {
  const { isChargeable, clientNoun, tradePartnerFilter, onUpdateSwap } = opts;
  return [
    column<Row>({ header: "#", data: ({ index }) => index, w: "40px" }),
    column<Row>({ header: "Item", data: ({ swap }) => swap.originalProjectItem.item.name, w: "160px" }),
    column<Row>({
      header: "Bid Type",
      data: ({ swap }) => <Tag type="neutral" text={swap.isPlanBasedSwap ? "Plan-Based" : "Unit-Based"} />,
    }),
    column<Row>({
      header: "Current Product",
      data: ({ swap }) => (
        <ProductCard materialVariant={swap.originalMaterialVariant} location={swap.originalProjectItem.location} />
      ),
      w: 2,
    }),
    column<Row>({
      header: "New Product",
      data: ({ swap }) => <ProductCard materialVariant={swap.proposedMaterialVariant} />,
      w: 2,
    }),
    column<Row>({
      header: "Trade",
      data: ({ swap, form }) =>
        swap.canEditTradePartner.allowed ? (
          <TradePartnerSelectField
            label="Trade"
            labelStyle="hidden"
            filter={tradePartnerFilter}
            value={swap.proposedTradePartner}
            onSelect={(id) => {
              form.proposedTradePartnerId.set(id);
              void onUpdateSwap();
            }}
          />
        ) : (
          tradeName(swap.addedLineItem)
        ),
    }),
    numericColumn<Row>({
      header: "Cost Change",
      // Only a plan-based swap needs its delta typed in; a unit-based one comes from the new product's cost.
      data: ({ swap, form }) =>
        swap.canEditCost.allowed ? (
          <BoundNumberField
            field={form.totalCostInCents}
            type="cents"
            label="Cost Change"
            labelStyle="hidden"
            placeholder="Enter Cost"
            onBlur={onUpdateSwap}
            onEnter={onUpdateSwap}
          />
        ) : (
          <ReviewImpactedPrice valueInCents={swap.totalCostInCents} />
        ),
    }),
    ...(isChargeable
      ? [
          numericColumn<Row>({
            header: `${clientNoun} Price`,
            data: ({ swap, form }) =>
              swap.canEditPrice.allowed ? (
                <BoundNumberField
                  field={form.totalPriceInCents}
                  type="cents"
                  label={`${clientNoun} Price`}
                  labelStyle="hidden"
                  placeholder="Enter Price"
                  onBlur={onUpdateSwap}
                  onEnter={onUpdateSwap}
                />
              ) : (
                <ReviewImpactedPrice valueInCents={swap.totalPriceInCents} />
              ),
          }),
        ]
      : []),
  ];
}
